/**
 * Classification Config Modal
 *
 * Shows built-in regex patterns (read-only) and allows users to add
 * custom patterns. Includes "Test Pattern" and "Re-classify All" features.
 */

import { useState, useMemo, useCallback } from 'react';
import { useProjectStore } from '../../stores/projectStore';
import { useUIStore } from '../../stores/uiStore';
import {
  reclassifyEntities,
  getClassificationStats,
} from '../../domain/layout/classify';
import { reclassifyAndBind } from '../../domain/layout/parentBind';
import type { ClassificationPattern, TopologyLevel } from '../../domain/layout/types';
import './ClassificationConfigModal.css';

const TOPOLOGY_LEVELS: TopologyLevel[] = [
  'station', 'inverter', 'mppt', 'dccb', 'string', 'module', 'row', 'zone',
];

const LEVEL_COLORS: Record<string, string> = {
  station: '#ef4444',
  inverter: '#3b82f6',
  mppt: '#06b6d4',
  dccb: '#f97316',
  string: '#22c55e',
  module: '#a855f7',
  row: '#ec4899',
  zone: '#14b8a6',
  infrastructure: '#6b7280',
  unknown: '#9ca3af',
  unclassified: '#4b5563',
};

// Built-in patterns (read-only display)
const BUILTIN_DISPLAY = [
  { id: 'fmt-01', regex: '^(\\d+)\\.(\\d+)\\.(DCCB\\d+)\\.(STR\\d+)', level: 'string', desc: '2.1.DCCB03.STR06 (Sta.MPPT.DCCB.STR)' },
  { id: 'fmt-02', regex: '^(\\d+)\\.(DCCB\\d+)\\.(STR\\d+)', level: 'string', desc: '1.DCCB01.STR09' },
  { id: 'fmt-03', regex: '^(\\d+)\\.(INV\\d+)\\.(?:Str|STR)(\\d+)', level: 'string', desc: '1.INV01.Str01' },
  { id: 'fmt-04', regex: '^(INV\\d+)\\.(?:Str|STR)(\\d+)', level: 'string', desc: 'INV1.Str1(27)MPPT1' },
  { id: 'fmt-05', regex: '^(\\d+)\\.(\\d+)\\.(\\d+)\\.(?:Str|STR)(\\d+)', level: 'string', desc: '1.08.22.Str7' },
  { id: 'fmt-06', regex: '^(\\d+)\\.(\\d+)\\.(?:Str|STR)(\\d+)', level: 'string', desc: '1.01.Str01' },
  { id: 'fmt-07', regex: '^TR(\\d+)[-.]?INV(\\d+)[-.]?STR(\\d+)', level: 'string', desc: 'TR1-INV01-STR07' },
  { id: 'fmt-08', regex: '^(INV\\d+)[-.]?(DCCB\\d+)', level: 'dccb', desc: 'INV1-DCCB1' },
  { id: 'fmt-09', regex: '^(\\d+)\\.(DCCB\\d+)$', level: 'dccb', desc: '1.DCCB01' },
  { id: 'fmt-10', regex: '^(\\d+)\\.(ACB\\d+)', level: 'dccb', desc: '1.ACB01' },
  { id: 'fmt-11', regex: '^([A-Z])\\.(\\d+)\\.(\\d+)$', level: 'zone', desc: 'M.19.10' },
  { id: 'fmt-12', regex: '^(\\d+)\\.(\\d+)$', level: 'string', desc: '1.1' },
  { id: 'fmt-13', regex: '^W-Row(\\d+)\\s*Column(\\d+)', level: 'module', desc: 'W-Row2 Column17' },
];

interface CustomPatternRow {
  id: string;
  regex: string;
  level: TopologyLevel;
  /** Capture-group → field mapping carried over from the stored config
   *  (schema-compiled patterns have these; losing them on Apply degrades
   *  the pattern to a level-only chip that feeds no suggestion fields). */
  captureGroups?: Record<string, number>;
  /** The regex as loaded from config — captureGroups indices are only
   *  trustworthy while the regex is unchanged. */
  origRegex?: string;
  testResult?: { matches: number; error?: string };
}

let nextId = 1;

export default function ClassificationConfigModal() {
  const closeModal = useUIStore((s) => s.closeModal);
  const siteLayout = useProjectStore((s) => s.data.siteLayout);
  const updateConfig = useProjectStore((s) => s.updateSiteLayoutConfig);
  const updateEntities = useProjectStore((s) => s.updateSiteLayoutEntities);

  // Initialize custom patterns from existing config
  const [customPatterns, setCustomPatterns] = useState<CustomPatternRow[]>(() => {
    if (!siteLayout?.classificationConfig) return [];
    return siteLayout.classificationConfig
      .filter((p) => !p.builtin)
      .map((p) => ({
        id: p.id,
        regex: p.regex,
        level: p.level as TopologyLevel,
        captureGroups: p.captureGroups,
        origRegex: p.regex,
      }));
  });

  // Current classification stats
  const currentStats = useMemo(() => {
    if (!siteLayout) return null;
    return getClassificationStats(siteLayout.textEntities as any);
  }, [siteLayout]);

  // Preview stats (after re-classify with custom patterns)
  const [previewStats, setPreviewStats] = useState<Record<string, number> | null>(null);
  const [reclassifying, setReclassifying] = useState(false);

  const handleAddPattern = () => {
    setCustomPatterns((prev) => [
      ...prev,
      { id: `custom-${nextId++}`, regex: '', level: 'string' },
    ]);
  };

  const handleRemovePattern = (id: string) => {
    setCustomPatterns((prev) => prev.filter((p) => p.id !== id));
  };

  const handleUpdatePattern = (id: string, field: 'regex' | 'level', value: string) => {
    setCustomPatterns((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: value, testResult: undefined } : p)),
    );
  };

  const handleTestPattern = (id: string) => {
    if (!siteLayout) return;
    const pattern = customPatterns.find((p) => p.id === id);
    if (!pattern) return;

    try {
      const re = new RegExp(pattern.regex, 'i');
      let matches = 0;
      for (const entity of siteLayout.textEntities) {
        if (re.test(entity.normalizedText)) matches++;
      }
      setCustomPatterns((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, testResult: { matches } } : p,
        ),
      );
    } catch (err) {
      setCustomPatterns((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, testResult: { matches: 0, error: (err as Error).message } } : p,
        ),
      );
    }
  };

  const buildConfigPatterns = useCallback((): ClassificationPattern[] => {
    return customPatterns
      .filter((p) => p.regex.trim().length > 0)
      .map((p, i) => ({
        id: p.id,
        regex: p.regex,
        level: p.level,
        // Preserve capture groups only while the regex is unchanged —
        // after an edit the stored group indices may point at the wrong groups.
        captureGroups:
          p.captureGroups && p.regex === p.origRegex ? p.captureGroups : {},
        priority: i + 1,
        builtin: false,
      }));
  }, [customPatterns]);

  const handlePreview = () => {
    if (!siteLayout) return;
    setReclassifying(true);
    const config = buildConfigPatterns();
    try {
      const reclassified = reclassifyEntities(siteLayout.textEntities as any, config);
      setPreviewStats(getClassificationStats(reclassified));
    } catch (err) {
      console.error('Re-classify failed:', err);
    }
    setReclassifying(false);
  };

  const handleApply = () => {
    if (!siteLayout) return;
    const config = buildConfigPatterns();
    // Re-classify and update both config and entities
    const reclassified = reclassifyAndBind(siteLayout.textEntities as any, config);
    updateConfig(config);
    updateEntities(reclassified as any, { reclassified: true });
    setPreviewStats(null);
    closeModal();
  };

  if (!siteLayout) {
    closeModal();
    return null;
  }

  return (
    <div className="ccm-backdrop" onClick={(e) => e.target === e.currentTarget && closeModal()}>
      <div className="ccm-modal">
        {/* Header */}
        <div className="ccm-header">
          <h3>Classification Patterns</h3>
          <button className="ccm-close" onClick={closeModal} type="button">&times;</button>
        </div>

        <div className="ccm-body">
          {/* Current stats */}
          <div className="ccm-section">
            <div className="ccm-section-title">Current Classification</div>
            <div className="ccm-stats">
              {currentStats &&
                Object.entries(currentStats)
                  .filter(([, count]) => count > 0)
                  .map(([level, count]) => (
                    <span
                      key={level}
                      className="ccm-stat-chip"
                      style={{ borderColor: LEVEL_COLORS[level] || '#6b7280' }}
                    >
                      <span className="ccm-stat-dot" style={{ background: LEVEL_COLORS[level] || '#6b7280' }} />
                      {level}: {count}
                    </span>
                  ))}
            </div>
          </div>

          {/* Built-in patterns */}
          <div className="ccm-section">
            <div className="ccm-section-title">Built-in Patterns (read-only)</div>
            <div className="ccm-pattern-list ccm-pattern-list--readonly">
              {BUILTIN_DISPLAY.map((p) => (
                <div key={p.id} className="ccm-pattern-row ccm-pattern-row--readonly">
                  <code className="ccm-pattern-regex">{p.regex}</code>
                  <span className="ccm-pattern-level" style={{ color: LEVEL_COLORS[p.level] }}>
                    {p.level}
                  </span>
                  <span className="ccm-pattern-desc">{p.desc}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Custom patterns */}
          <div className="ccm-section">
            <div className="ccm-section-header">
              <span className="ccm-section-title">Custom Patterns</span>
              <button type="button" className="ccm-btn ccm-btn--add" onClick={handleAddPattern}>
                + Add Pattern
              </button>
            </div>

            {customPatterns.length === 0 ? (
              <p className="ccm-empty">No custom patterns. Add one to override built-in classification.</p>
            ) : (
              <div className="ccm-pattern-list">
                {customPatterns.map((p) => (
                  <div key={p.id} className="ccm-pattern-row">
                    <input
                      type="text"
                      className="ccm-pattern-input"
                      placeholder="Regex pattern..."
                      value={p.regex}
                      onChange={(e) => handleUpdatePattern(p.id, 'regex', e.target.value)}
                    />
                    <select
                      className="ccm-pattern-select"
                      value={p.level}
                      onChange={(e) => handleUpdatePattern(p.id, 'level', e.target.value)}
                    >
                      {TOPOLOGY_LEVELS.map((l) => (
                        <option key={l} value={l}>{l}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="ccm-btn ccm-btn--test"
                      onClick={() => handleTestPattern(p.id)}
                      disabled={!p.regex.trim()}
                    >
                      Test
                    </button>
                    <button
                      type="button"
                      className="ccm-btn ccm-btn--remove"
                      onClick={() => handleRemovePattern(p.id)}
                    >
                      &times;
                    </button>
                    {p.testResult && (
                      <span className={`ccm-test-result ${p.testResult.error ? 'ccm-test-result--error' : ''}`}>
                        {p.testResult.error
                          ? `Error: ${p.testResult.error}`
                          : `${p.testResult.matches} match${p.testResult.matches !== 1 ? 'es' : ''}`}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Preview stats */}
          {previewStats && (
            <div className="ccm-section">
              <div className="ccm-section-title">Preview (after re-classify)</div>
              <div className="ccm-stats">
                {Object.entries(previewStats)
                  .filter(([, count]) => count > 0)
                  .map(([level, count]) => (
                    <span
                      key={level}
                      className="ccm-stat-chip"
                      style={{ borderColor: LEVEL_COLORS[level] || '#6b7280' }}
                    >
                      <span className="ccm-stat-dot" style={{ background: LEVEL_COLORS[level] || '#6b7280' }} />
                      {level}: {count}
                    </span>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="ccm-footer">
          <button type="button" className="ccm-btn ccm-btn--preview" onClick={handlePreview} disabled={reclassifying}>
            Preview Re-classify
          </button>
          <div className="ccm-footer-spacer" />
          <button type="button" className="ccm-btn ccm-btn--cancel" onClick={closeModal}>
            Cancel
          </button>
          <button type="button" className="ccm-btn ccm-btn--apply" onClick={handleApply}>
            Apply & Re-classify
          </button>
        </div>
      </div>
    </div>
  );
}
